/**
 * ProyectoPublico.jsx
 * Vista pública que ve el cliente para seguir el avance de su proyecto.
 * Acceso: https://app.tuaplicacion.cl/?ptoken=xxx
 * No requiere autenticación — el token es el mecanismo de acceso.
 * Solo lectura: los datos llegan desde la RPC proyecto_publico(token), que
 * devuelve únicamente campos autorizados (sin notas internas, email ni RUT).
 * Para comentar, el cliente debe ingresar al Portal de Clientes.
 */
import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import {
  CheckCircle2, Circle, ChevronDown, ChevronRight,
  Loader, AlertCircle, FileText, Calendar, Clock,
  FolderKanban, BarChart2, MessageSquare,
} from "lucide-react";
import { calcularProgreso } from "../lib/progreso";

const C = {
  bg:"#f8fafc", white:"#ffffff", border:"#e2e8f0",
  accent:"#29abe2", accentL:"#e8f5fc",
  text:"#1e293b", sub:"#64748b", light:"#94a3b8",
  ok:"#10b981", okL:"#d1fae5",
  err:"#ef4444", errL:"#fee2e2",
  warn:"#f59e0b", warnL:"#fef3c7",
  purple:"#818cf8", purpleL:"#eef2ff",
};

const COLS = [
  {key:"Por hacer",   color:"#94a3b8", bg:"#f1f5f9"},
  {key:"En progreso", color:"#f59e0b", bg:"#fef3c7"},
  {key:"En revisión", color:"#818cf8", bg:"#eef2ff"},
  {key:"Completado",  color:"#10b981", bg:"#d1fae5"},
];

const TIPO_COLOR = {
  Feature:         {color:"#29abe2", bg:"#e8f5fc"},
  Fix:             {color:"#ef4444", bg:"#fee2e2"},
  Integración:     {color:"#f59e0b", bg:"#fef3c7"},
  Diseño:          {color:"#818cf8", bg:"#eef2ff"},
  Consultoría:     {color:"#10b981", bg:"#d1fae5"},
  Infraestructura: {color:"#64748b", bg:"#f1f5f9"},
};

const EST_COLOR = {
  Activo:     {color:C.accent,  bg:C.accentL},
  "En pausa": {color:C.warn,    bg:C.warnL},
  Completado: {color:C.ok,      bg:C.okL},
  Cancelado:  {color:C.err,     bg:C.errL},
};

const fmtDate = (d) => d
  ? new Date(d+"T12:00:00").toLocaleDateString("es-CL",
      {day:"2-digit", month:"long", year:"numeric"})
  : "—";

const diasRestantes = (d) => {
  if (!d) return null;
  return Math.ceil((new Date(d+"T12:00:00") - new Date()) / 864e5);
};

export default function ProyectoPublico() {
  const ptoken = new URLSearchParams(window.location.search).get("ptoken");

  const [proyecto, setProyecto] = useState(null);
  const [tareas,   setTareas]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  useEffect(() => {
    if (!ptoken) { setError("Link inválido o sin token."); setLoading(false); return; }

    (async () => {
      // Proyecto + tareas + subtareas en una sola llamada de solo lectura
      const { data: p, error: pe } = await supabase
        .rpc("proyecto_publico", { p_token: ptoken });

      if (pe) {
        setError(`No se pudo cargar el proyecto: ${pe.message}`);
        setLoading(false); return;
      }
      if (!p) {
        setError("Proyecto no encontrado. Verifica el link.");
        setLoading(false); return;
      }

      setProyecto(p);
      setTareas(p.tareas || []);
      setLoading(false);
    })();
  }, [ptoken]);

  // ── Cálculos de progreso
  const totalTareas    = tareas.length;
  const completadas    = tareas.filter(t => t.estado==="Completado").length;
  const pct            = calcularProgreso(completadas, totalTareas);
  const totalSubs      = tareas.reduce((s,t) => s+(t.subtareas?.length||0), 0);
  const subsDone       = tareas.reduce((s,t) => s+(t.subtareas?.filter(x=>x.completada).length||0), 0);

  // ── Loading
  if (loading) return (
    <div style={{minHeight:"100vh", background:C.bg, display:"flex",
      alignItems:"center", justifyContent:"center",
      fontFamily:"'DM Sans',sans-serif"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');@keyframes spin{to{transform:rotate(360deg)}}.spinning{animation:spin 1s linear infinite}`}</style>
      <div style={{textAlign:"center", color:C.sub}}>
        <Loader size={28} color={C.accent} className="spinning"
          style={{margin:"0 auto 12px"}}/>
        <div style={{fontSize:14}}>Cargando proyecto...</div>
      </div>
    </div>
  );

  // ── Error
  if (error) return (
    <div style={{minHeight:"100vh", background:C.bg, display:"flex",
      alignItems:"center", justifyContent:"center",
      fontFamily:"'DM Sans',sans-serif", padding:24}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');`}</style>
      <div style={{background:C.white, border:`1px solid ${C.border}`, borderRadius:12,
        padding:"32px 28px", maxWidth:420, textAlign:"center",
        boxShadow:"0 4px 20px #0000000f"}}>
        <AlertCircle size={36} color={C.err} style={{margin:"0 auto 14px"}}/>
        <div style={{fontWeight:700, fontSize:16, marginBottom:8}}>Proyecto no disponible</div>
        <div style={{fontSize:14, color:C.sub, lineHeight:1.6}}>{error}</div>
        <div style={{marginTop:20, fontSize:12, color:C.light}}>
          ¿Problemas? Contacta a{" "}
          <strong style={{color:C.accent}}>contacto@tuaplicacion.cl</strong>
        </div>
      </div>
    </div>
  );

  const est   = EST_COLOR[proyecto.estado] || EST_COLOR.Activo;
  const dias  = diasRestantes(proyecto.fecha_entrega);

  return (
    <div style={{minHeight:"100vh", background:"#f0f6fb",
      fontFamily:"'DM Sans',sans-serif", color:C.text}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        @keyframes fadeUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        .fade-up{animation:fadeUp .22s ease both}
      `}</style>

      {/* Header */}
      <div style={{background:C.white, borderBottom:`1px solid ${C.border}`,
        padding:"0 32px", height:56,
        display:"flex", alignItems:"center", justifyContent:"space-between",
        boxShadow:"0 1px 4px #0000000a"}}>
        <div style={{display:"flex", alignItems:"center", gap:10}}>
          <div style={{width:30, height:30, borderRadius:8, background:C.accent,
            display:"flex", alignItems:"center", justifyContent:"center"}}>
            <FolderKanban size={15} color="#fff"/>
          </div>
          <div>
            <span style={{fontWeight:800, fontSize:15, color:C.accent}}>tu</span>
            <span style={{fontWeight:800, fontSize:15, color:C.text}}>aplicacion</span>
          </div>
          <span style={{fontSize:11, color:C.sub, marginLeft:4}}>
            · Seguimiento de proyecto
          </span>
        </div>
        <span style={{padding:"3px 12px", borderRadius:20, fontSize:12,
          fontWeight:600, color:est.color, background:est.bg}}>
          {proyecto.estado}
        </span>
      </div>

      <div style={{maxWidth:740, margin:"0 auto", padding:"32px 20px"}}>

        {/* ── Card principal ── */}
        <div style={{background:C.white, border:`1px solid ${C.border}`, borderRadius:12,
          padding:"22px 24px", marginBottom:16, boxShadow:"0 1px 3px #0000000a"}}
          className="fade-up">
          <div style={{display:"flex", justifyContent:"space-between",
            alignItems:"flex-start", marginBottom:18}}>
            <div>
              <div style={{fontSize:10, fontWeight:600, color:C.light,
                letterSpacing:1, textTransform:"uppercase", marginBottom:6}}>
                Proyecto de Desarrollo
              </div>
              <div style={{fontWeight:800, fontSize:24, color:C.text, marginBottom:4}}>
                {proyecto.nombre}
              </div>
              <div style={{fontSize:13, color:C.sub}}>
                Cliente: <strong style={{color:C.text}}>{proyecto.cliente}</strong>
              </div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontWeight:800, fontSize:32, color:pct===100?C.ok:C.accent}}>
                {pct}%
              </div>
              <div style={{fontSize:11, color:C.sub, marginTop:2}}>completado</div>
            </div>
          </div>

          {/* Barra progreso grande */}
          <div style={{marginBottom:18}}>
            <div style={{height:8, background:C.bg, borderRadius:4, overflow:"hidden"}}>
              <div style={{height:"100%", borderRadius:4,
                background:pct===100?C.ok:C.accent,
                width:`${pct}%`, transition:"width .5s ease"}}/>
            </div>
          </div>

          {/* Meta grid */}
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:14,
            paddingTop:16, borderTop:`1px solid ${C.border}`}}>
            {[
              ["Tareas",      `${completadas}/${totalTareas}`, <BarChart2 size={13} color={C.accent}/>],
              ["Subtareas",   totalSubs>0?`${subsDone}/${totalSubs}`:"—",
                <CheckCircle2 size={13} color={C.ok}/>],
              ["Inicio",      fmtDate(proyecto.fecha_inicio),
                <Calendar size={13} color={C.sub}/>],
              ["Entrega",     fmtDate(proyecto.fecha_entrega),
                <Clock size={13} color={dias!==null&&dias<7?C.err:C.sub}/>],
            ].map(([l, v, icon]) => (
              <div key={l}>
                <div style={{display:"flex", alignItems:"center", gap:4,
                  fontSize:10, fontWeight:600, color:C.light,
                  letterSpacing:.5, textTransform:"uppercase", marginBottom:5}}>
                  {icon}{l}
                </div>
                <div style={{fontSize:13, fontWeight:500}}>{v}</div>
              </div>
            ))}
          </div>

          {/* Aviso días restantes */}
          {dias !== null && dias <= 7 && (
            <div style={{marginTop:14, padding:"10px 14px", borderRadius:8,
              background:dias<0?C.errL:C.warnL,
              border:`1px solid ${dias<0?C.err:C.warn}40`,
              fontSize:12, color:dias<0?C.err:C.warn, fontWeight:500}}>
              {dias<0
                ? `⚠ La fecha de entrega venció hace ${Math.abs(dias)} día${Math.abs(dias)!==1?"s":""}.`
                : dias===0
                  ? "⚠ La entrega es hoy."
                  : `⚠ Quedan ${dias} día${dias!==1?"s":""} para la entrega.`}
            </div>
          )}
        </div>

        {/* ── Descripción del proyecto ── */}
        {proyecto.descripcion && (
          <div style={{background:C.white, border:`1px solid ${C.border}`,
            borderRadius:12, padding:"15px 20px", marginBottom:16,
            boxShadow:"0 1px 3px #0000000a"}}>
            <div style={{fontSize:10, fontWeight:600, color:C.light,
              letterSpacing:.5, textTransform:"uppercase", marginBottom:5}}>
              Descripción
            </div>
            <div style={{fontSize:13, color:C.sub, lineHeight:1.6, whiteSpace:"pre-wrap"}}>
              {proyecto.descripcion}
            </div>
          </div>
        )}

        {/* ── Tareas por columna ── */}
        {COLS.map(col => {
          const colTareas = tareas.filter(t => t.estado===col.key);
          if (colTareas.length===0) return null;
          return (
            <div key={col.key} style={{marginBottom:14}}>
              {/* Header sección */}
              <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:8}}>
                <span style={{width:8, height:8, borderRadius:"50%",
                  background:col.color, display:"inline-block"}}/>
                <span style={{fontWeight:700, fontSize:13, color:C.text}}>
                  {col.key}
                </span>
                <span style={{padding:"1px 8px", borderRadius:10, fontSize:11,
                  fontWeight:600, color:col.color, background:col.bg}}>
                  {colTareas.length}
                </span>
              </div>

              {/* Tarjetas */}
              <div style={{display:"flex", flexDirection:"column", gap:8}}>
                {colTareas.map(tarea => (
                  <TareaPublica key={tarea.id} tarea={tarea}/>
                ))}
              </div>
            </div>
          );
        })}

        {/* Mensaje si no hay tareas */}
        {tareas.length===0 && (
          <div style={{background:C.white, border:`1px solid ${C.border}`,
            borderRadius:12, padding:"36px 24px", textAlign:"center",
            boxShadow:"0 1px 3px #0000000a"}}>
            <div style={{fontSize:13, color:C.sub}}>
              El equipo aún no ha cargado las tareas del proyecto.
            </div>
          </div>
        )}

        {/* ── Comentar requiere sesión en el portal ── */}
        <div style={{background:C.white, border:`1px solid ${C.border}`, borderRadius:12,
          padding:"16px 20px", marginTop:16, display:"flex", alignItems:"center", gap:14,
          flexWrap:"wrap", boxShadow:"0 1px 3px #0000000a"}}>
          <MessageSquare size={20} color={C.accent} aria-hidden="true"/>
          <div style={{flex:1, minWidth:200}}>
            <div style={{fontWeight:700, fontSize:14}}>¿Tienes comentarios o consultas?</div>
            <div style={{fontSize:12, color:C.sub, marginTop:2}}>
              Ingresa al Portal de Clientes para conversar con el equipo sobre este proyecto o sus tareas.
            </div>
          </div>
          <a href={`/portal?proyecto=${encodeURIComponent(proyecto.id)}`}
            style={{padding:"8px 16px", borderRadius:8, background:C.accent, color:"#fff",
              fontWeight:700, fontSize:13, textDecoration:"none"}}>
            Ir al portal
          </a>
        </div>

        {/* ── Completado ── */}
        {pct===100 && (
          <div style={{background:C.okL, border:`1px solid ${C.ok}40`,
            borderRadius:12, padding:"28px 24px", textAlign:"center",
            marginTop:16}}>
            <div style={{width:52, height:52, borderRadius:"50%", background:C.ok,
              display:"flex", alignItems:"center", justifyContent:"center",
              margin:"0 auto 14px",
              boxShadow:`0 4px 16px ${C.ok}50`}}>
              <CheckCircle2 size={26} color="#fff"/>
            </div>
            <div style={{fontWeight:800, fontSize:18, color:C.ok, marginBottom:8}}>
              ¡Proyecto completado!
            </div>
            <div style={{fontSize:14, color:C.sub, lineHeight:1.6, maxWidth:380, margin:"0 auto"}}>
              Todas las tareas han sido completadas. El equipo se pondrá en contacto
              contigo para coordinar la entrega final.
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{textAlign:"center", marginTop:36, paddingTop:18,
          borderTop:`1px solid ${C.border}`}}>
          <div style={{fontSize:13, fontWeight:700}}>
            <span style={{color:C.accent}}>tu</span>
            <span style={{color:C.sub}}>aplicacion</span>
            <span style={{color:C.light, fontWeight:400}}>
              {" "}· Empresa de Desarrollo de Software · Chile
            </span>
          </div>
          <div style={{fontSize:11, color:C.light, marginTop:5}}>
            contacto@tuaplicacion.cl · app.tuaplicacion.cl
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tarjeta de tarea (solo lectura)
function TareaPublica({ tarea }) {
  const [expandido, setExpandido] = useState(false);
  const tc   = TIPO_COLOR[tarea.tipo] || TIPO_COLOR.Feature;
  const subs = tarea.subtareas || [];
  const done = subs.filter(s => s.completada).length;
  const pct  = subs.length>0 ? Math.round(done/subs.length*100) : 0;

  return (
    <div style={{background:C.white, border:`1px solid ${C.border}`,
      borderRadius:10, overflow:"hidden", boxShadow:"0 1px 3px #0000000a"}}>

      {/* Cabecera */}
      <div style={{display:"flex", alignItems:"center", gap:10, padding:"13px 16px"}}>
        {/* Ícono estado */}
        {tarea.estado==="Completado"
          ? <CheckCircle2 size={16} color={C.ok} style={{flexShrink:0}}/>
          : <Circle size={16} color={C.light} style={{flexShrink:0}}/>}

        <div style={{flex:1, minWidth:0}}>
          <div style={{fontWeight:600, fontSize:14, color:C.text,
            textDecoration:tarea.estado==="Completado"?"line-through":"none",
            lineHeight:1.4}}>
            {tarea.descripcion}
          </div>
          <div style={{display:"flex", alignItems:"center", gap:8, marginTop:5}}>
            <span style={{padding:"1px 7px", borderRadius:4, fontSize:10,
              fontWeight:600, color:tc.color, background:tc.bg}}>
              {tarea.tipo}
            </span>
            {tarea.horas_estimadas>0 && (
              <span style={{fontSize:11, color:C.light,
                display:"flex", alignItems:"center", gap:3}}>
                <Clock size={10}/> {tarea.horas_estimadas}h est.
              </span>
            )}
            {tarea.horas_reales>0 && (
              <span style={{fontSize:11, color:C.sub,
                display:"flex", alignItems:"center", gap:3}}>
                · {tarea.horas_reales}h reales
              </span>
            )}
          </div>
        </div>

        {/* Toggle subtareas */}
        {subs.length>0 && (
          <button onClick={() => setExpandido(!expandido)}
            style={{display:"flex", alignItems:"center", gap:6,
              background:"transparent", border:"none", cursor:"pointer",
              color:C.sub, fontSize:12, fontFamily:"'DM Sans',sans-serif",
              padding:"4px 8px", borderRadius:6, transition:"background .12s",
              flexShrink:0}}
            onMouseEnter={e => e.currentTarget.style.background=C.bg}
            onMouseLeave={e => e.currentTarget.style.background="transparent"}>
            <span style={{fontWeight:500}}>{done}/{subs.length}</span>
            {expandido ? <ChevronDown size={13}/> : <ChevronRight size={13}/>}
          </button>
        )}
      </div>

      {/* Barra progreso subtareas */}
      {subs.length>0 && (
        <div style={{padding:"0 16px 11px",
          borderTop:`1px solid ${C.border}`, paddingTop:10}}>
          <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:6}}>
            <div style={{flex:1, height:3, background:C.bg, borderRadius:2}}>
              <div style={{height:"100%", borderRadius:2, background:C.ok,
                width:`${pct}%`, transition:"width .3s"}}/>
            </div>
            <span style={{fontSize:11, color:C.sub, minWidth:30, textAlign:"right"}}>
              {pct}%
            </span>
          </div>

          {/* Lista subtareas expandida */}
          {expandido && (
            <div style={{display:"flex", flexDirection:"column", gap:5, marginTop:6}}>
              {subs.map(sub => (
                <div key={sub.id}
                  style={{display:"flex", alignItems:"flex-start", gap:8}}>
                  {sub.completada
                    ? <CheckCircle2 size={13} color={C.ok}
                        style={{flexShrink:0, marginTop:2}}/>
                    : <Circle size={13} color={C.light}
                        style={{flexShrink:0, marginTop:2}}/>}
                  <span style={{fontSize:12,
                    color:sub.completada?C.light:C.sub,
                    textDecoration:sub.completada?"line-through":"none",
                    lineHeight:1.5}}>
                    {sub.descripcion}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
